/**
 * PlatbyPanel — the money of one reservation (P6-e2): the lease, what is paid and what is left, the gateway attempts with their refunds,
 * the credits, and the two things staff do — "Označit jako zaplaceno" (a payment taken at the branch, at `pujcovna:write`) and
 * "Vrátit…" on an attempt (a refund through its gateway, at `pujcovna:admin`). Every number is the server's (`GET …/platby`, one
 * snapshot, always re-read on open): the panel words it and never works out a balance or what may be refunded. It sits in the reservation
 * form and in the list's "Platby" dialog; it is not part of the form's save — each action is its own call.
 */

import { useEffect, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Field } from '@/components/Field';
import { LinkButton } from '@/components/LinkButton';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import { usePlatbyQuery, useRucniUhraduMutation, type Platba, type PlatbyPrehled, type Rezervace } from './api';
import { datumCas } from './brana-helpers';
import { POZNAMKA_MAX, ZDROJ_UHRADY, castkaChyba, duvodBezUhrady, lzeZadatUhradu, metodaNazev, parseCastka, providerNazev, stavPlatby, stavVraceni } from './platby-helpers';
import { VraceniPlatbyModal } from './VraceniPlatbyModal';

const kc = (n: number | null | undefined) => (n == null ? '—' : `${formatInt(n)} Kč`);

function Cislo({ label, value, testid, tone }: { label: string; value: string; testid: string; tone?: 'warn' }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">{label}</dt>
      <dd data-testid={testid} className={`font-mono text-[15px] tracking-[-0.01em] ${tone === 'warn' ? 'text-warn' : 'text-ink'}`}>
        {value}
      </dd>
    </div>
  );
}

/** The branch payment: the box starts at what is left; it reseeds (the caller's `key`) when the balance changes. */
function RucniUhrada({ rezervaceId, zbyva, onBusy }: { rezervaceId: number; zbyva: number; onBusy?: (busy: boolean) => void }) {
  const uhrada = useRucniUhraduMutation(rezervaceId);
  const { confirm, dialog } = useConfirm();
  const [castka, setCastka] = useState(String(zbyva));
  const [poznamka, setPoznamka] = useState('');
  const busy = uhrada.isPending;
  const chyba = castkaChyba(castka, zbyva);
  // the dialog around the panel must not close while the payment is in flight (its toast hangs on this observer)
  useEffect(() => {
    onBusy?.(busy);
    return () => onBusy?.(false);
  }, [busy, onBusy]);

  const submit = async () => {
    if (busy) return;
    const n = parseCastka(castka);
    if (chyba || n == null) return void toast.error(chyba ?? 'Zadejte částku v celých korunách.');
    const ok = await confirm({
      title: (
        <>
          Zapsat platbu <em>{formatInt(n)} Kč</em>?
        </>
      ),
      description: 'Platba přijatá na pobočce se zapíše k rezervaci. Zápis nelze smazat — chybu opraví jen vrácení peněz.',
      confirmLabel: 'Zapsat platbu',
    });
    if (!ok) return;
    uhrada.mutate(
      { castka: n, poznamka: poznamka.trim() },
      {
        onSuccess: (res) => {
          toast.ok(res.zbyva > 0 ? `Platba zapsána, zbývá ${formatInt(res.zbyva)} Kč.` : 'Platba zapsána, rezervace je zaplacená.');
          setPoznamka('');
        },
        onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se zapsat platbu.'}`),
      },
    );
  };

  return (
    <div data-testid="platby-rucne" className="flex flex-col gap-3 border-t border-line pt-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
        <Field
          label="Přijatá částka"
          state={chyba && castka.trim() !== '' ? 'error' : undefined}
          hint={chyba && castka.trim() !== '' ? chyba : 'Hotovost nebo karta na pobočce.'}
          hintTone={chyba && castka.trim() !== '' ? 'error' : 'neutral'}
        >
          <TextInput inputMode="numeric" value={castka} suffix="Kč" disabled={busy} data-testid="platby-rucne-castka" onChange={(e) => setCastka(e.currentTarget.value)} />
        </Field>
        <Field label="Poznámka" optional="volitelné">
          <TextInput value={poznamka} maxLength={POZNAMKA_MAX} disabled={busy} data-testid="platby-rucne-poznamka" onChange={(e) => setPoznamka(e.currentTarget.value)} />
        </Field>
        <Button variant="secondary" size="sm" type="button" disabled={busy || chyba != null} data-testid="platby-rucne-zapsat" onClick={() => void submit()}>
          Označit jako zaplaceno
        </Button>
      </div>
      {dialog}
    </div>
  );
}

function PlatbaRadek({ platba, canAdmin, onVratit }: { platba: Platba; canAdmin: boolean; onVratit: (p: Platba) => void }) {
  const stav = stavPlatby(platba.stav);
  return (
    <li data-testid={`platba-${platba.id}`} className="flex flex-col gap-1.5 border-b border-line py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono text-[12px] text-ink">№ {platba.id}</span>
          <span className="text-[13px] text-ink">
            {providerNazev(platba.provider)} · {metodaNazev(platba.metoda)}
          </span>
          <Badge tone={stav.tone} size="sm">
            {stav.label}
          </Badge>
        </span>
        <span className="font-mono text-[12px] text-ink-2">{kc(platba.castka)}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[12px] text-ink-3">
        <span>
          {datumCas(platba.vytvoreno_at)}
          {platba.prijato > 0 && ` · přijato ${kc(platba.prijato)}`}
          {platba.vraceno > 0 && ` · vráceno ${kc(platba.vraceno)}`}
          {platba.stav === 'ceka' && platba.expiruje_at && ` · platí do ${datumCas(platba.expiruje_at)}`}
        </span>
        {canAdmin && platba.lze_vratit > 0 && (
          <Button variant="secondary" size="sm" type="button" data-testid={`platba-vratit-${platba.id}`} onClick={() => onVratit(platba)}>
            Vrátit…
          </Button>
        )}
      </div>
      {platba.vraceni.length > 0 && (
        <ul className="flex flex-col gap-0.5 pl-3 text-[12px] text-ink-2">
          {platba.vraceni.map((v) => (
            <li key={v.id} data-testid={`vraceni-${v.id}`} className={v.stav === 'selhalo' ? 'text-red-text' : ''}>
              Vrácení {kc(v.castka)} · {stavVraceni(v.stav)} · {datumCas(v.vytvoreno_at)}
              {v.uzivatel ? ` · ${v.uzivatel}` : ''}
              {v.duvod ? ` — ${v.duvod}` : ''}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Obsah({ rezervace, prehled, canWrite, canAdmin, onBusy }: { rezervace: Rezervace; prehled: PlatbyPrehled; canWrite: boolean; canAdmin: boolean; onBusy?: (busy: boolean) => void }) {
  const [vraci, setVraci] = useState<Platba | null>(null);
  const duvod = duvodBezUhrady(rezervace, prehled);
  const bezPlateb = prehled.platby.length === 0 && prehled.uhrady.length === 0;

  return (
    <>
      <dl className="grid grid-cols-3 gap-4" data-testid="platby-souhrn">
        <Cislo label="K úhradě" value={kc(prehled.cena)} testid="platby-cena" />
        <Cislo label="Zaplaceno" value={kc(prehled.zaplaceno)} testid="platby-zaplaceno" />
        <Cislo label="Zbývá" value={kc(prehled.cena == null ? null : prehled.zbyva)} testid="platby-zbyva" tone={prehled.zbyva > 0 ? 'warn' : undefined} />
      </dl>
      <p className="text-[12px] text-ink-3">Nájemné bez kauce — kauce se skládá na pobočce při předání.</p>

      {prehled.vyjimky_otevrene > 0 && (
        <p data-testid="platby-vyjimky" className="border border-warn bg-warn-soft px-3 py-2 text-[12.5px] text-ink">
          K rezervaci {prehled.vyjimky_otevrene === 1 ? 'čeká 1 položka' : `čekají ${prehled.vyjimky_otevrene} položky`} ve frontě plateb k řešení (přeplatek, platba po zrušení…).{' '}
          {canAdmin && (
            <LinkButton as="a" href="/pujcovna/sprava?nastroj=vyjimky" target="_blank" rel="noreferrer">
              Otevřít frontu
            </LinkButton>
          )}
        </p>
      )}

      {lzeZadatUhradu(canWrite, rezervace, prehled) ? (
        // the box reseeds with the balance after a payment
        <RucniUhrada key={prehled.zbyva} rezervaceId={rezervace.id} zbyva={prehled.zbyva} onBusy={onBusy} />
      ) : (
        duvod && (
          <p data-testid="platby-bez-uhrady" className="text-[12.5px] text-ink-2">
            {duvod}
          </p>
        )
      )}

      {bezPlateb ? (
        <p data-testid="platby-prazdne" className="text-[12.5px] text-ink-3">
          Zatím žádné platby.
        </p>
      ) : (
        <>
          {prehled.platby.length > 0 && (
            <section aria-label="Pokusy o platbu" className="flex flex-col gap-1">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Platby přes bránu</span>
              <ul data-testid="platby-seznam">
                {prehled.platby.map((p) => (
                  <PlatbaRadek key={p.id} platba={p} canAdmin={canAdmin} onVratit={setVraci} />
                ))}
              </ul>
            </section>
          )}
          {prehled.uhrady.length > 0 && (
            <section aria-label="Připsané částky" className="flex flex-col gap-1">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Připsané částky</span>
              <ul data-testid="uhrady-seznam">
                {prehled.uhrady.map((u) => (
                  <li key={u.id} data-testid={`uhrada-${u.id}`} className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-line py-2 text-[12.5px] last:border-b-0">
                    <span className="text-ink-2">
                      {datumCas(u.vytvoreno_at)} · {ZDROJ_UHRADY[u.zdroj]}
                      {u.uzivatel ? ` · ${u.uzivatel}` : ''}
                      {u.poznamka ? ` — ${u.poznamka}` : ''}
                    </span>
                    <span className="font-mono text-ink">{kc(u.castka)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* one instance per attempt: the amount box is seeded once from it */}
      {vraci && <VraceniPlatbyModal key={vraci.id} platba={vraci} open onOpenChange={(open) => !open && setVraci(null)} />}
    </>
  );
}

interface PlatbyPanelProps {
  rezervace: Rezervace;
  canWrite: boolean;
  canAdmin: boolean;
  /** Told when a payment is in flight, so the surrounding dialog can refuse to close meanwhile. */
  onBusy?: (busy: boolean) => void;
}

export function PlatbyPanel({ rezervace, canWrite, canAdmin, onBusy }: PlatbyPanelProps) {
  const query = usePlatbyQuery(rezervace.id);
  return (
    <section data-testid="platby-panel" aria-label="Platby" className="flex flex-col gap-3 border border-line p-4">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Platby</div>
      {/* drawn only from a read that finished after the panel opened: a cached balance must not seed the payment box */}
      {query.isPending || (!query.isError && !query.isFetchedAfterMount) ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      ) : query.isError || !query.data ? (
        <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-ink-2" data-testid="platby-chyba">
          Platby se nepodařilo načíst.
          <Button variant="secondary" size="sm" type="button" onClick={() => void query.refetch()}>
            Zkusit znovu
          </Button>
        </div>
      ) : (
        <Obsah rezervace={rezervace} prehled={query.data} canWrite={canWrite} canAdmin={canAdmin} onBusy={onBusy} />
      )}
    </section>
  );
}
